import apiClient from "../api/client";
import { eventNameDataLayer } from "../constants/track";
import { trackDataLayer } from "./track";

/**
 * GA4 `sign_up` (GROWTH-8100).
 *
 * Members register in Keycloak, so the dashboard never sees the sign-up itself. The backend marks every new account
 * and the dashboard bootstrap reports it as `ga_sign_up_pending`. On that load the dashboard claims the sign-up, and
 * only the request the backend answers `send: true` pushes the event: a reload, a second tab or a second device is
 * answered `send: false`. Sending it from the browser keeps the member's GA client id and session attribution.
 *
 * The event carries `method` (the Keycloak identity provider, `keycloak` for a password, or `unknown`) and
 * `signup_source`, never an email or a name.
 */

export const GA_SIGN_UP_CLAIM_URL = "/members/api-v2/my-account/ga-sign-up";

// The user whose sign-up was already claimed in this page session, so a second bootstrap that lands while the
// claim is in flight does not ask again.
let claimedFor = null;

export const hasGaSignUpPending = profile => profile?.ga_sign_up_pending === true && Boolean(profile.user_id);

// The page without its query string or hash, which can carry tokens or ids.
const signupPage = () => `${window.location.origin}${window.location.pathname}`;

export const reportGaSignUp = profile => {
  if (!hasGaSignUpPending(profile) || claimedFor === profile.user_id) {
    return Promise.resolve(false);
  }
  claimedFor = profile.user_id;

  return apiClient({
    method: "post",
    responseType: "json",
    url: GA_SIGN_UP_CLAIM_URL
  })
    .then(res => {
      const data = res?.data?.data || {};
      if (data.send !== true) {
        return false;
      }

      trackDataLayer(eventNameDataLayer.signUp, {
        method: data.method,
        signup_source: data.signup_source,
        signup_page: signupPage(),
        user_id: String(profile.user_id)
      });

      return true;
    })
    .catch(() => {
      // Nothing was sent. If the claim never reached the backend the account is still marked; let a later
      // bootstrap in this page session ask again.
      claimedFor = null;

      return false;
    });
};
